import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { BarChart, RefreshCw, TrendingUp, DollarSign, AlertTriangle } from 'lucide-react';
import { R_STRATEGIES } from '../../constants';
import DashboardAssetDetail from './DashboardAssetDetail';
import MetricCard from './MetricCard';
import MiniSparkline from '../common/MiniSparkline';
import { getPortfolioMci, getEPRSummary } from '../../services/api';

// Numeric format helpers
const formatCurrencyK = (value) => {
  const num = Number(value || 0);
  const inK = num / 1000;
  // Keep one decimal place, clamp negative zero
  const s = (Math.abs(inK) < 0.05 ? 0 : inK).toFixed(1);
  return `$${s}K`;
};

const formatPercent = (value, digits = 1) => {
  const num = Number(value || 0);
  return `${num.toFixed(digits)}%`;
};

const formatInt = (value) => Number(value || 0).toLocaleString();


const CircularEconomyDashboardContent = ({ assets, dashboardSelectedAssetId, setDashboardSelectedAssetId, onReloadAssets, isLoadingAssets, error }) => {
  // Metrics UI presentation toggle (only affects cards; charts always visible)
  const [metricsUiMode, setMetricsUiMode] = useState(() => {
    if (typeof window === 'undefined') return 'normal';
    try {
      return localStorage.getItem('dashboardMetricsUiMode') || 'normal';
    } catch (e) {
      return 'normal';
    }
  });

  const [portfolioMci, setPortfolioMci] = useState({ average_mci: null, count: 0 });
  const [isLoadingPortfolioMci, setIsLoadingPortfolioMci] = useState(false);
  const [portfolioMciError, setPortfolioMciError] = useState(null);
  const [eprSummary, setEprSummary] = useState({ scheme: 'EPR', collected: 0, placed_on_market: 0 });
  const [isLoadingEpr, setIsLoadingEpr] = useState(false);
  const [eprError, setEprError] = useState(null);


useEffect(() => {
  let cancelled = false;
  setIsLoadingPortfolioMci(true); setPortfolioMciError(null);
  getPortfolioMci()
    .then((data) => { if (!cancelled) setPortfolioMci(data || { average_mci: null, count: 0 }); })
    .catch((err) => { if (!cancelled) setPortfolioMciError(err?.message || 'Failed to load portfolio MCI'); })
    .finally(() => { if (!cancelled) setIsLoadingPortfolioMci(false); });
  return () => { cancelled = true; };
}, []);

useEffect(() => {
  let cancelled = false;
  setIsLoadingEpr(true); setEprError(null);
  getEPRSummary({ scheme: 'WEEE' })
    .then((data) => { if (!cancelled) setEprSummary(data || { scheme: 'WEEE', placed_on_market: 0, collected: 0, recycled: 0 }); })
    .catch((err) => { if (!cancelled) setEprError(err?.message || 'Failed to load EPR summary'); })
    .finally(() => { if (!cancelled) setIsLoadingEpr(false); });
  return () => { cancelled = true; };
}, []);

const dashboardData = useMemo(() => {

    // Data sources from API:
    // totalValue: Sum of all assets' current_market_value
    const totalValue = assets.reduce((sum, a) => sum + Number(a.current_market_value || 0), 0);

    // avgCircularity: Average of all assets' AI-estimated circularityScore
    const avgCircularity = assets.length > 0 ? assets.reduce((sum, a) => sum + Number(a.circularity_score || 0), 0) / assets.length : 0;

    // totalCircularPotential: Sum of all assets' AI-estimated circular_potential
    const totalCircularPotential = assets.reduce((sum, a) => sum + Number(a.circular_potential || 0), 0);

    // lowConditionCount: Count of assets with conditionRating < 2.0
    const lowConditionCount = assets.filter(a => Number(a.condition_rating || 0) < 2.0).length;

    // strategyDistribution: Count of assets by current_strategy (from API)
    const strategyDistribution = assets.reduce((acc, a) => {
      const strategy = a.current_strategy || 'R7';
      acc[strategy] = (acc[strategy] || 0) + 1;
      return acc;
    }, {});

    // AI Recommendations Summary
    const aiRecommendations = assets.reduce((acc, a) => {
      if (a.ai_recommended_r_strategy) {
        acc.recommendedStrategies[a.ai_recommended_r_strategy] = (acc.recommendedStrategies[a.ai_recommended_r_strategy] || 0) + 1;
      }
      if (a.ai_recommended_end_of_life_strategy) {
        acc.endOfLifeStrategies[a.ai_recommended_end_of_life_strategy] = (acc.endOfLifeStrategies[a.ai_recommended_end_of_life_strategy] || 0) + 1;
      }
      return acc;
    }, { recommendedStrategies: {}, endOfLifeStrategies: {} });

    // DPP coverage: % assets with a passport payload present (legacy or split)
    const dppCoveredCount = assets.filter(a => a.product_passport || a.dpp_public).length;
    const dppCoveragePct = assets.length > 0 ? (dppCoveredCount / assets.length) * 100 : 0;

    const result = {
      totalValue,
      avgCircularity,
      totalCircularPotential,
      lowConditionCount,
      strategyDistribution,
      aiRecommendations,
      dppCoveredCount,
      dppCoveragePct,
    };

    return result;
  }, [assets]);

  const selectedAsset = assets.find(a => a.id === dashboardSelectedAssetId);

  const handleAssetClick = useCallback((assetId) => {
    setDashboardSelectedAssetId(assetId);
  }, [setDashboardSelectedAssetId]);

  const handleBack = useCallback(() => {
    setDashboardSelectedAssetId(null);
  }, [setDashboardSelectedAssetId]);

  const totalAssets = assets.length;

  // Sparkline data for metrics chart mode (purely presentational)
  const marketValues = useMemo(() => assets.map(a => Number(a.current_market_value || 0)).filter(v => Number.isFinite(v)), [assets]);
  const circularPotentials = useMemo(() => assets.map(a => Number(a.circular_potential || 0)).filter(v => Number.isFinite(v)), [assets]);
  const circularityScores = useMemo(() => assets.map(a => Number(a.circularity_score || 0)).filter(v => Number.isFinite(v) && v >= 0 && v <= 100), [assets]);
  const attentionFlags = useMemo(() => assets.map(a => (Number(a.condition_rating || 0) < 2.0 ? 1 : 0)), [assets]);
  const dppCoverageFlags = useMemo(() => assets.map(a => ((a && (a.product_passport || (a.dpp_public && Object.keys(a.dpp_public || {}).length > 0))) ? 100 : 0)), [assets]);
  const eprCollectionRate = useMemo(() => {
    const placed = Number(eprSummary?.placed_on_market || 0);
    const collected = Number(eprSummary?.collected || 0);
    return placed > 0 ? (collected / placed) * 100 : 0;
  }, [eprSummary?.placed_on_market, eprSummary?.collected]);

  useEffect(() => {
    try { localStorage.setItem('dashboardMetricsUiMode', metricsUiMode); } catch(e) {}
  }, [metricsUiMode]);

  return (
    <div className="container mx-auto px-4 py-8 overflow-hidden">
      
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        
        <h2 className="text-3xl font-extrabold text-gray-800 flex items-center hidden md:block">
          <BarChart className="w-7 h-7 mr-3 text-cyan-600" />
          Circular Economy Dashboard Overview
        </h2>  
        <h2 className="text-3xl font-bold text-gray-800 flex items-center md:hidden" style={{ fontSize: '1.5rem' }}>
          <BarChart className="w-7 h-7 mr-3 text-cyan-600" />
          Assets Overview
        </h2>

        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden hidden md:block" role="group" aria-label="Toggle metrics UI">
          <button
            type="button"
            onClick={() => setMetricsUiMode('normal')}
            aria-pressed={metricsUiMode === 'normal'}
            className={`px-3 py-1.5 text-sm font-medium ${metricsUiMode === 'normal' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Metrics: Normal
          </button>
          <button
            type="button"
            onClick={() => setMetricsUiMode('chart')}
            aria-pressed={metricsUiMode === 'chart'}
            className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 ${metricsUiMode === 'chart' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
           Metrics: Chart
          </button>
        </div>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden md:hidden" role="group" aria-label="Toggle metrics UI">
          <button
            type="button"
            onClick={() => setMetricsUiMode('normal')}
            aria-pressed={metricsUiMode === 'normal'}
            className={`px-3 py-1.5 text-sm font-medium ${metricsUiMode === 'normal' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
          Normal
          </button>
          <button
            type="button"
            onClick={() => setMetricsUiMode('chart')}
            aria-pressed={metricsUiMode === 'chart'}
            className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 ${metricsUiMode === 'chart' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
          Chart
          </button>
        </div>
        
      </div>

      {(error && totalAssets === 0) && (
        <div className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 flex items-start">
          <AlertTriangle className="w-5 h-5 mr-2 mt-0.5" />
          <div>
            <p className="font-medium">Failed to Load Assets</p>
            <p className="text-sm opacity-90">{String(error)}</p>
          </div>
        </div>
      )}

      {isLoadingAssets && totalAssets === 0 && !error && (
        <div className="mb-4 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-700">Loading assets…</div>
      )}

      {/* Row 1: Performance & Financial */}
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Performance & Financial</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
          <MetricCard
            title="Total Asset Value"
            value={formatCurrencyK(dashboardData.totalValue)}
            icon={<DollarSign />}
            color="text-green-600"
            trend="+4.5% MoM"
            tooltip="Sum of current market value across all assets in the portfolio."
          >
            {metricsUiMode === 'chart' && (
              <MiniSparkline data={marketValues} width={140} height={48} label="Total Value (K)" />
            )}
          </MetricCard>
          <MetricCard
            title="Total Circular Potential"
            value={formatCurrencyK(dashboardData.totalCircularPotential)}
            icon={<TrendingUp />}
            color="text-indigo-600"
            trend="AI Estimated"
            tooltip="Sum of estimated recoverable value across all assets (scaled by circularity and category caps)."
          >
            {metricsUiMode === 'chart' && (
              <MiniSparkline data={circularPotentials} width={140} height={48} label="Potential" />
            )}
          </MetricCard>
          <MetricCard
            title="Assets Needing Attention"
            value={formatInt(dashboardData.lowConditionCount)}
            icon={<AlertTriangle />}
            color="text-red-600"
            trend="Condition < 2.0"
            tooltip="Number of assets with condition rating below 2.0 (0–5 scale); may require triage soon."
          >
            {metricsUiMode === 'chart' && (
              <MiniSparkline data={attentionFlags} width={140} height={48} label="Flags" />
            )}
          </MetricCard>
          <MetricCard
            title="Avg. Circularity Score"
            value={formatPercent(dashboardData.avgCircularity, 1)}
            icon={<RefreshCw />}
            color="text-cyan-600"
            trend="AI Estimated"
            tooltip="Average of AI-estimated (or fallback) circularity scores across all assets."
          >
            {metricsUiMode === 'chart' && (
              <MiniSparkline data={circularityScores} width={140} height={48} label="Circularity" />
            )}
          </MetricCard>
          <MetricCard
            title="Portfolio Average MCI"
            value={portfolioMci.average_mci != null ? formatPercent(portfolioMci.average_mci, 1) : formatPercent(dashboardData.avgCircularity, 1)}
            icon={<RefreshCw />}
            color="text-indigo-600"
            trend={isLoadingPortfolioMci ? 'Loading…' : (portfolioMciError ? 'Error' : `from ${portfolioMci.count} assets`)}
            tooltip={isLoadingPortfolioMci ? 'Loading Material Circularity Indicator…' : (portfolioMciError ? 'Unable to load MCI. Showing fallback if available.' : `Material Circularity Indicator: portfolio-level average from ${portfolioMci.count} assets.`)}
          >
            {metricsUiMode === 'chart' && (
              <MiniSparkline data={circularityScores} width={140} height={48} label="MCI" />
            )}
          </MetricCard>
        </div>
      </div>

      {/* Row 2: Compliance & Sustainability */}
      <div className="mb-8">

        <h3 className="text-lg font-semibold text-gray-700 mb-3">Compliance & Sustainability</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          <MetricCard
            className="bg-white p-6 rounded-xl shadow-xl border-t-4 border-purple-500"
            title="DPP Coverage"
            value={`${dashboardData.dppCoveredCount}/${assets.length} (${formatPercent(dashboardData.dppCoveragePct, 0)})`}
            icon={<BarChart />}
            color="text-emerald-600"
            trend="EU DPP readiness"
            tooltip="Share of assets with a Digital Product Passport payload present."
          >
            {metricsUiMode === 'chart' && (
              <MiniSparkline data={dppCoverageFlags} width={140} height={48} label="DPP Coverage" />
            )}
          </MetricCard>
          <MetricCard
            title={`${eprSummary.scheme} Collection Rate`}
            value={(eprSummary.placed_on_market > 0)
              ? formatPercent((eprSummary.collected / eprSummary.placed_on_market) * 100, 0)
              : '0%'}
            icon={<BarChart />}
            color="text-purple-600"
            trend={isLoadingEpr ? 'Loading…' : (eprError ? 'Error' : `${eprSummary.collected}/${eprSummary.placed_on_market}`)}
            tooltip={isLoadingEpr
              ? 'Loading EPR summary (WEEE)…'
              : (eprError
                ? 'Unable to load EPR summary.'
                : 'Extended Producer Responsibility: collected as a share of placed-on-market.')}
          >
            {metricsUiMode === 'chart' && (
              <MiniSparkline data={[eprCollectionRate]} width={140} height={48} label="Collection Rate" />
            )}
          </MetricCard>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <div className="lg:col-span-2">
          <DashboardAssetDetail asset={selectedAsset} onBack={handleBack} assets={assets} onSelectAsset={handleAssetClick} onReloadAssets={onReloadAssets} />
        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-xl border-t-4 border-indigo-500">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Strategy Distribution ({totalAssets} Assets) </h3>
            <div className="space-y-3">
              {Object.entries(R_STRATEGIES).map(([key, config]) => {
                const count = dashboardData.strategyDistribution[key] || 0;
                const percentage = totalAssets > 0 ? (count / totalAssets) * 100 : 0;
                return (
                  <div key={key}>
                    <div className="flex justify-between text-sm font-medium text-gray-700 mb-1">
                      <span>{config.name} ({key})</span>
                      <span>{count} Assets({percentage.toFixed(0)}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full ${config.color}`} style={{ width: `${percentage}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI Recommendations Summary */}
          <div className="bg-white p-6 rounded-xl shadow-xl border-t-4 border-green-500">
            <h3 className="text-xl font-bold text-gray-800 mb-4">AI Strategy Recommendations</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Recommended R Strategies</h4>
                <div className="space-y-2">
                  {Object.entries(dashboardData.aiRecommendations.recommendedStrategies).map(([strategy, count]) => (
                    <div key={strategy} className="flex justify-between text-sm">
                      <span>{strategy}</span>
                      <span className="font-medium text-blue-600">{count} assets</span>
                    </div>
                  ))}
                  {Object.keys(dashboardData.aiRecommendations.recommendedStrategies).length === 0 && (
                    <p className="text-sm text-gray-500">No AI recommendations yet</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">End-of-Life Strategies</h4>
                <div className="space-y-2">
                  {Object.entries(dashboardData.aiRecommendations.endOfLifeStrategies).slice(0, 3).map(([strategy, count]) => (
                    <div key={strategy} className="flex justify-between text-sm">
                      <span className="truncate" title={strategy}>{strategy}</span>
                      <span className="font-medium text-green-600">{count}</span>
                    </div>
                  ))}
                  {Object.keys(dashboardData.aiRecommendations.endOfLifeStrategies).length === 0 && (
                    <p className="text-sm text-gray-500">No AI recommendations yet</p>
                  )}
                </div>
              </div>

          </div>
        </div>

        </div>

        </div>
    
    </div>
  );
};

export default CircularEconomyDashboardContent;
